import React, { useMemo } from 'react';
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useUserProfile } from "@/hooks/useUserProfile";
import { useDashboardStats } from "@/hooks/useDashboardStats";
import { useDashboardCampaigns } from "@/hooks/useDashboardCampaigns";
import { useAllInfluencers } from "@/hooks/useAllInfluencers";
import { useDashboardPayments } from "@/hooks/useDashboardPayments";
import { useDashboardBrands } from "@/hooks/useDashboardBrands";
import { useCampaigns } from "@/hooks/useCampaigns";
import { formatNumber } from "@/lib/utils";
import { Building2 } from 'lucide-react';
import { DashboardStats } from './dashboard/DashboardStats';
import { GoogleSheetsExportButton } from './dashboard/GoogleSheetsExportButton';
import { StatusBadge } from '@/components/status/StatusBadge';
import { TopPerformingCampaignsCard } from './dashboard/TopPerformingCampaignsCard';
import { TopPerformersCard } from './dashboard/TopPerformersCard';

const calculatePerformanceScore = (influencer: any) => {
  const engagementWeight = 0.4;
  const roiWeight = 0.4;
  const campaignWeight = 0.2;

  const engagement = Number(influencer.engagement) || 0;
  const rawRoi = influencer.avgROI ? String(influencer.avgROI).replace(/[^0-9.-]/g, "") : "0";
  const roiParsed = parseFloat(rawRoi);
  const roi = isNaN(roiParsed) ? 0 : roiParsed;
  const campaigns = Number(influencer.campaigns) || 0;

  const score = engagement * engagementWeight + roi * roiWeight + campaigns * campaignWeight;
  return isNaN(score) ? 0 : score;
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: userProfile } = useUserProfile();

  const userName = userProfile?.name || user?.user_metadata?.full_name || user?.user_metadata?.name || user?.email?.split('@')[0] || '';

  // Fetch data using hooks
  const { data: stats, isLoading: statsLoading } = useDashboardStats();
  const { data: recentCampaigns, isLoading: campaignsLoading } = useDashboardCampaigns();
  const { data: allCampaigns = [] } = useCampaigns();
  const {
    data: allInfluencers = [],
    isLoading: influencersLoading,
    isError: influencersError,
  } = useAllInfluencers();
  const { data: paymentSummary, isLoading: paymentsLoading } = useDashboardPayments();
  const { data: brandsCount, isLoading: brandsLoading } = useDashboardBrands();

  // Map campaign ID to its brand logo and brand name
  const campaignBrandMap = useMemo(() => {
    const map = new Map<string, { brand_logo?: string | null; brand_name?: string }>();
    allCampaigns.forEach((c) => {
      map.set(c.id, {
        brand_logo: c.brand_logo,
        brand_name: c.brand_name || c.brand,
      });
    });
    return map;
  }, [allCampaigns]);

  // Process & sort top influencers to match the ranking on /influencers/top (campaign participants only)
  const topInfluencers = useMemo(() => {
    // If brand has no campaigns, there are no campaign participants
    if (!allCampaigns || allCampaigns.length === 0) {
      return [];
    }

    const campaignInfluencers = allInfluencers.filter((inf) => (Number(inf.campaigns) || 0) > 0);

    const scored = campaignInfluencers.map((inf) => {
      const isPicUrl = inf.avatar && (inf.avatar.startsWith('http') || inf.avatar.startsWith('/') || inf.avatar.startsWith('data:'));

      return {
        ...inf,
        profilePicUrl: isPicUrl ? inf.avatar : null,
        performanceScore: calculatePerformanceScore(inf),
      };
    });
    

    return scored.sort((a, b) => b.performanceScore - a.performanceScore);
  }, [allCampaigns, allInfluencers]);

  return (
    <div className="p-6 space-y-6">

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600">
            {userName ? `Welcome ${userName}! ` : 'Welcome back! '}Here&apos;s what&apos;s happening with your campaigns.
          </p>
        </div>
        <div className="flex space-x-3">
          <GoogleSheetsExportButton
            recentCampaigns={recentCampaigns || []}
            topInfluencers={topInfluencers || []}
            paymentSummary={paymentSummary || null}
          />
          <button
            className="px-4 py-2 text-sm font-medium text-white bg-[#A655F8] rounded-lg "
            onClick={() => navigate('/campaigns')}
          >
            New Campaign
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <DashboardStats
        stats={stats}
        statsLoading={statsLoading}
        brandsCount={brandsCount ?? null}
        brandsLoading={brandsLoading}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Campaigns */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-semibold text-gray-900">Recent Campaigns</h2>
            <button
              className="text-sm text-purple-600 hover:text-purple-700"
              onClick={() => navigate('/campaigns')}
            >
              View All
            </button>
          </div>
          <div className="space-y-4">
            {campaignsLoading
              ? Array(3).fill(null).map((_, idx) => (
                <div key={idx} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg animate-pulse h-16" />
              ))
              : recentCampaigns && recentCampaigns.length > 0 ? recentCampaigns.map((campaign, index) => {
                const brandInfo = campaignBrandMap.get(campaign.id);
                const brandLogo = brandInfo?.brand_logo;
                const brandName = brandInfo?.brand_name || campaign.brand;

                return (
                  <div
                    key={campaign.id || index}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg transition-colors hover:bg-purple-50 cursor-pointer"
                    onClick={() => navigate(`/campaigns/${campaign.id}`)}
                    tabIndex={0}
                    role="button"
                    aria-label={`Open campaign ${campaign.name}`}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') navigate(`/campaigns/${campaign.id}`); }}
                  >
                    <div className="flex items-center gap-3.5 flex-1 min-w-0 mr-4">
                      {brandLogo ? (
                        <div className="w-10 h-10 rounded-lg border border-gray-200 bg-white shrink-0 shadow-xs flex items-center justify-center overflow-hidden">
                          <img
                            src={brandLogo}
                            alt={brandName || campaign.name}
                            className="w-full h-full object-contain"
                          />
                        </div>
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-[#FAF5FF] border border-[#F3E8FF] flex items-center justify-center shrink-0 shadow-xs text-[#A655F8]">
                          <Building2 className="w-5 h-5" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-gray-900 truncate">{campaign.name}</h3>
                        <div className="flex items-center space-x-4 mt-1.5">
                          <span className="text-xs text-gray-500">{campaign.influencers} influencers</span>
                          <span className="text-xs text-gray-500">{formatNumber(campaign.reach)} reach</span>
                          <span className="text-xs text-gray-500">{campaign.engagement !== undefined && campaign.engagement !== null ? `${campaign.engagement}% engagement` : "N/A"}</span>
                        </div>
                      </div>
                    </div>
                    <StatusBadge status={campaign.displayStatus} className="text-[11px] shrink-0" />
                  </div>
                );
              })
                : <div className="text-gray-400 text-center py-8">
                  No recent campaigns. Create one to get started!
                </div>
            }
          </div>
        </div>

        {/* Top Performers */}
        <TopPerformersCard
          influencers={topInfluencers}
          isLoading={influencersLoading}
          hasError={influencersError}
        />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <TopPerformingCampaignsCard />
      </div>
    </div>
  );
};

export default Dashboard;
